<template>
  <div class="fw">
    <ContentSkeleton v-if="loading && !data" variant="list" :rows="4" />
    <p v-else-if="denied" class="dw-empty">{{ t("denied") }}</p>
    <p v-else-if="error" class="dw-empty">{{ $t("dashboardWidgets.loadError") }}</p>

    <!-- Este mes: cobrado, pagado y neto contra el mes anterior. -->
    <template v-else-if="view === 'month'">
      <p v-if="!monthRows.length" class="dw-empty">{{ t("noMovements") }}</p>
      <div v-for="r in monthRows" :key="r.currency || 'none'" class="fw-month">
        <div class="fw-month__cur">{{ cur(r.currency) }}</div>
        <div class="fw-kpis">
          <router-link to="/financial/collects" class="fw-kpi">
            <span class="fw-kpi__label">{{ t("collected") }}</span>
            <span class="fw-kpi__value">{{ money(r.inCurrent) }}</span>
            <span class="fw-kpi__delta" :class="deltaClass(r.inCurrent, r.inPrevious)">{{ deltaText(r.inCurrent, r.inPrevious) }}</span>
          </router-link>
          <router-link to="/financial/payments" class="fw-kpi">
            <span class="fw-kpi__label">{{ t("paid") }}</span>
            <span class="fw-kpi__value">{{ money(r.outCurrent) }}</span>
            <span class="fw-kpi__delta is-neutral">{{ t("prevMonth", { v: money(r.outPrevious) }) }}</span>
          </router-link>
          <div class="fw-kpi">
            <span class="fw-kpi__label">{{ t("net") }}</span>
            <span class="fw-kpi__value" :class="r.inCurrent - r.outCurrent < 0 ? 'text-danger' : ''">{{ money(r.inCurrent - r.outCurrent) }}</span>
            <span class="fw-kpi__delta is-neutral">{{ t("prevMonth", { v: money(r.inPrevious - r.outPrevious) }) }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- Flujo de caja: entradas y salidas por mes (una moneda). -->
    <template v-else-if="view === 'cashflow'">
      <div class="fw-caption">
        <span>{{ cur(chartCurrency) }}</span>
        <span class="fw-legend"><i class="fw-sw fw-sw--in"></i>{{ t("in") }}</span>
        <span class="fw-legend"><i class="fw-sw fw-sw--out"></i>{{ t("out") }}</span>
      </div>
      <p v-if="!cashflowMonths.length" class="dw-empty">{{ t("noMovements") }}</p>
      <div v-else class="fw-pairs">
        <div v-for="m in cashflowMonths" :key="m.month" class="fw-pair" :title="`${m.label} · ${t('in')}: ${money(m.in)} · ${t('out')}: ${money(m.out)}`">
          <span class="fw-pair__net" :class="m.in - m.out < 0 ? 'is-neg' : ''">{{ compact(m.in - m.out) }}</span>
          <span class="fw-pair__bars">
            <span class="fw-pair__bar fw-pair__bar--in" :style="{ height: `${pctOf(m.in, cashflowMax)}%` }"></span>
            <span class="fw-pair__bar fw-pair__bar--out" :style="{ height: `${pctOf(m.out, cashflowMax)}%` }"></span>
          </span>
          <span class="fw-pair__label">{{ m.label }}</span>
        </div>
      </div>
    </template>

    <!-- Pedidos de pago sin pagar. -->
    <template v-else-if="view === 'payments-pending'">
      <div class="fw-totals">
        <router-link v-for="r in data.totals" :key="r.currency || 'none'" to="/financial/payments" class="fw-total">
          <span class="fw-total__value">{{ money(r.amount) }} <small>{{ cur(r.currency) }}</small></span>
          <span class="fw-total__label">{{ t("requests", { n: r.count }) }}</span>
          <span v-if="r.overdueCount" class="fw-total__warn">{{ t("overdue", { n: r.overdueCount, v: money(r.overdueAmount) }) }}</span>
        </router-link>
      </div>
      <p v-if="!data.totals.length" class="fw-ok"><i class="fas fa-check-circle" aria-hidden="true"></i> {{ t("noPending") }}</p>
      <ul v-else class="fw-list">
        <li v-for="p in data.next" :key="p._id" class="fw-row">
          <span class="fw-row__date" :class="{ 'is-late': p.overdue }">{{ shortDate(p.date) }}</span>
          <span class="fw-row__body">
            <router-link :to="`/financial/payments/view/${p._id}`" class="fw-row__title">{{ p.contact || p.name }}</router-link>
            <span class="fw-row__text">{{ p.name }}</span>
          </span>
          <span class="fw-row__amount">{{ money(p.amount) }} <small>{{ cur(p.currency) }}</small></span>
        </li>
      </ul>
    </template>

    <!-- Por cobrar / adelantos: total por moneda y los que más deben. -->
    <template v-else-if="view === 'receivables' || view === 'advances'">
      <div class="fw-totals">
        <div v-for="r in data.totals" :key="r.currency || 'none'" class="fw-total">
          <span class="fw-total__value">{{ money(r.amount) }} <small>{{ cur(r.currency) }}</small></span>
          <span class="fw-total__label">{{ t(view === "advances" ? "proformsWithAdvance" : "operations", { n: r.count }) }}</span>
        </div>
      </div>
      <p v-if="!data.totals.length" class="fw-ok"><i class="fas fa-check-circle" aria-hidden="true"></i> {{ t("nothingPending") }}</p>
      <ul v-else class="fw-list">
        <li v-for="(r, idx) in topRows" :key="idx" class="fw-row">
          <span class="fw-row__body">
            <router-link v-if="r.to" :to="r.to" class="fw-row__title">{{ r.title }}</router-link>
            <span v-else class="fw-row__title">{{ r.title }}</span>
            <span v-if="r.text" class="fw-row__text">{{ r.text }}</span>
          </span>
          <span class="fw-row__amount">{{ money(r.amount) }} <small>{{ cur(r.currency) }}</small></span>
        </li>
      </ul>
      <p v-if="data.excluded" class="fw-note">{{ t("excluded", { n: data.excluded }) }}</p>
    </template>

    <!-- Por pagar a proveedores. -->
    <template v-else-if="view === 'payables'">
      <div class="fw-totals">
        <div v-for="r in data.totals" :key="r.currency || 'none'" class="fw-total">
          <span class="fw-total__value">{{ money(r.amount) }} <small>{{ cur(r.currency) }}</small></span>
          <span class="fw-total__label">{{ t(data.source === "expenses" ? "expensesCount" : "shippingsCount", { n: r.count }) }}</span>
        </div>
      </div>
      <p v-if="!data.totals.length" class="fw-ok"><i class="fas fa-check-circle" aria-hidden="true"></i> {{ t("nothingPending") }}</p>
      <p v-if="data.excluded" class="fw-note">{{ t("excluded", { n: data.excluded }) }}</p>
    </template>

    <!-- Saldos de cuentas: por moneda, sin convertir. -->
    <template v-else-if="view === 'bank-balances'">
      <div class="fw-totals">
        <div v-for="r in balanceTotals" :key="r.currency || 'none'" class="fw-total">
          <span class="fw-total__value" :class="r.amount < 0 ? 'text-danger' : ''">{{ money(r.amount) }} <small>{{ cur(r.currency) }}</small></span>
          <span class="fw-total__label">{{ t("accounts", { n: r.count }) }}</span>
        </div>
      </div>
      <ul class="fw-list">
        <li v-for="a in data.accounts" :key="a._id" class="fw-row">
          <span class="fw-row__body"><span class="fw-row__title">{{ a.name }}</span></span>
          <span class="fw-row__amount" :class="a.balance < 0 ? 'text-danger' : ''">{{ money(a.balance) }} <small>{{ cur(a.currency) }}</small></span>
        </li>
      </ul>
    </template>

    <!-- Margen por mes. -->
    <template v-else-if="view === 'margins'">
      <p v-if="!marginSeries" class="dw-empty">{{ t("noMargins") }}</p>
      <template v-else>
        <div class="fw-kpis">
          <div class="fw-kpi">
            <span class="fw-kpi__label">{{ t("sales") }} · {{ cur(marginSeries.currency) }}</span>
            <span class="fw-kpi__value">{{ compact(marginSeries.sale) }}</span>
          </div>
          <div class="fw-kpi">
            <span class="fw-kpi__label">{{ t("margin") }}</span>
            <span class="fw-kpi__value" :class="marginSeries.margin < 0 ? 'text-danger' : ''">{{ compact(marginSeries.margin) }}</span>
          </div>
          <div class="fw-kpi">
            <span class="fw-kpi__label">{{ t("marginPct") }}</span>
            <span class="fw-kpi__value">{{ pct(marginSeries.margin, marginSeries.sale) }}</span>
          </div>
        </div>
        <div class="fw-caption"><span>{{ t("marginPctByMonth") }}</span></div>
        <div class="fw-chart">
          <WidgetChart type="column" :points="marginPoints" />
        </div>
      </template>
    </template>

    <!-- Operaciones con menos margen. -->
    <template v-else-if="view === 'low-margins'">
      <p v-if="!data.rows.length" class="dw-empty">{{ t("noMargins") }}</p>
      <ul v-else class="fw-list">
        <li v-for="r in data.rows" :key="r._id" class="fw-row">
          <span class="fw-row__pct" :class="r.pct < 0 ? 'is-neg' : r.pct < 5 ? 'is-low' : ''">{{ r.pct.toFixed(1) }}%</span>
          <span class="fw-row__body">
            <router-link :to="itemLink(data.basis, r._id, r.name)" class="fw-row__title">{{ r.name }}</router-link>
            <span class="fw-row__text">{{ r.client }}</span>
          </span>
          <span class="fw-row__amount">{{ money(r.margin) }} <small>{{ cur(r.currency) }}</small></span>
        </li>
      </ul>
      <p class="fw-note">{{ t("last90") }}</p>
    </template>
  </div>
</template>

<script>
/**
 * Widgets financieros (módulo de finanzas): este mes, flujo de caja, pedidos de pago,
 * por cobrar, por pagar, saldos de cuentas, márgenes, operaciones con menos margen y
 * adelantos. Datos de GET /api/financial/widgets/:key (agregaciones livianas). Nunca
 * se suman monedas distintas: una fila por moneda, y los gráficos van en una sola.
 */
import { wencesApi } from "@/api/wencesApi";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { formatNumberSync } from "@/utils/numberFormatter";
import { buildDynamicItemPrettySlug } from "@/utils/dynamicModelPaths";
import WidgetChart from "./WidgetChart.vue";
import { formatCompactNumber } from "../widgetData";

export default {
  name: "FinanceWidget",
  components: { ContentSkeleton, WidgetChart },
  props: {
    entry: { type: Object, required: true },
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return { loading: false, error: false, denied: false, data: null, currencies: {}, systemCurrency: null };
  },
  computed: {
    view() {
      return (this.entry.config && this.entry.config.view) || "month";
    },
    basis() {
      return this.config.basis || (this.entry.config && this.entry.config.basis) || "proforms";
    },
    locale() {
      return (this.$i18n && this.$i18n.locale) || "es";
    },
    monthRows() {
      return ((this.data && this.data.rows) || []).filter((r) => r.inCurrent || r.inPrevious || r.outCurrent || r.outPrevious);
    },
    /** Monedas que trae un gráfico (flujo o márgenes), la principal primero. */
    chartCurrencies() {
      const series = (this.data && this.data.series) || [];
      return series.map((s) => s.currency);
    },
    chartCurrency() {
      const list = this.chartCurrencies;
      if (this.config.currency && list.includes(this.config.currency)) return this.config.currency;
      if (this.systemCurrency && list.includes(this.systemCurrency)) return this.systemCurrency;
      return list[0] || null;
    },
    seriesOf() {
      return ((this.data && this.data.series) || []).find((s) => s.currency === this.chartCurrency) || null;
    },
    cashflowMonths() {
      const s = this.seriesOf;
      if (!s) return [];
      return s.months.map((m) => ({ ...m, label: this.monthLabel(m.month) }));
    },
    cashflowMax() {
      return Math.max(1, ...this.cashflowMonths.map((m) => Math.max(m.in, m.out)));
    },
    marginSeries() {
      return this.seriesOf;
    },
    marginPoints() {
      const s = this.marginSeries;
      if (!s) return [];
      return s.months.map((m) => ({ label: this.monthLabel(m.month), value: m.sale ? Math.round((m.margin / m.sale) * 1000) / 10 : 0 }));
    },
    balanceTotals() {
      const map = {};
      ((this.data && this.data.accounts) || []).forEach((a) => {
        const k = a.currency || "none";
        map[k] = map[k] || { currency: a.currency, amount: 0, count: 0 };
        map[k].amount += a.balance;
        map[k].count += 1;
      });
      return Object.values(map);
    },
    topRows() {
      const rows = (this.data && this.data.top) || [];
      if (this.view === "advances") {
        return rows.map((r) => ({ title: r.name, text: r.client, amount: r.amount, currency: r.currency, to: this.itemLink("proforms", r._id, r.name) }));
      }
      return rows.map((r) => ({
        title: r.contact,
        text: this.t("operations", { n: r.count }),
        amount: r.amount,
        currency: r.currency,
        to: r.contactId ? `/i/${buildDynamicItemPrettySlug("contact", r.contactId, r.contact)}` : null,
      }));
    },
    menuItems() {
      const items = [];
      if (this.view === "margins" || this.view === "low-margins") {
        items.push(
          { divider: true, key: "d0", label: this.$tn("dashboardWidgets.finance.basis") },
          { key: "basis:proforms", label: this.$tn("dashboardWidgets.finance.basisProforms"), active: this.basis === "proforms" },
          { key: "basis:shippings", label: this.$tn("dashboardWidgets.finance.basisShippings"), active: this.basis === "shippings" }
        );
      }
      if ((this.view === "cashflow" || this.view === "margins") && this.chartCurrencies.length > 1) {
        items.push({ divider: true, key: "d1", label: this.$tn("dashboardWidgets.finance.currency") });
        this.chartCurrencies.forEach((c) =>
          items.push({ key: `currency:${c}`, label: this.cur(c), active: c === this.chartCurrency })
        );
      }
      items.push({ divider: true, key: "d2" });
      items.push({ key: "open", label: this.$tn("dashboardWidgets.finance.open"), icon: "external-link-alt" });
      return items;
    },
    openPath() {
      if (this.view === "payments-pending") return "/financial/payments";
      if (this.view === "receivables" || this.view === "advances") return "/financial/collects";
      return "/financial";
    },
  },
  watch: {
    menuItems: {
      immediate: true,
      handler(items) {
        this.$emit("menu", items);
      },
    },
    basis() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    t(key, params) {
      return this.$t(`dashboardWidgets.finance.${key}`, params);
    },
    async load() {
      this.loading = true;
      this.error = false;
      this.denied = false;
      try {
        const params = { _: Date.now() };
        if (this.view === "margins" || this.view === "low-margins") params.basis = this.basis;
        const { data } = await wencesApi.get(`/api/financial/widgets/${this.view}`, { params });
        this.data = data.data;
        this.currencies = data.currencies || {};
        this.systemCurrency = data.systemCurrency || null;
      } catch (e) {
        if (e && e.response && e.response.status === 403) this.denied = true;
        else this.error = true;
      } finally {
        this.loading = false;
      }
    },
    cur(id) {
      if (!id) return this.t("noCurrency");
      return this.currencies[id] || "";
    },
    /** Montos de widget: sin decimales desde 1.000 (el formato del tenant puede traer 4). */
    money(v) {
      const n = Number(v) || 0;
      const digits = Math.abs(n) >= 1000 ? 0 : 2;
      try {
        return new Intl.NumberFormat(this.locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
      } catch (e) {
        return formatNumberSync(n) || "0";
      }
    },
    compact(v) {
      return formatCompactNumber(v);
    },
    pct(part, total) {
      if (!total) return "—";
      return `${(Math.round((part / total) * 1000) / 10).toFixed(1)}%`;
    },
    pctOf(v, max) {
      return v > 0 ? Math.max(2, Math.round((v / max) * 100)) : 0;
    },
    deltaText(cur, prev) {
      if (!prev) return this.t("prevMonth", { v: this.money(prev) });
      const d = Math.round(((cur - prev) / prev) * 100);
      return `${d >= 0 ? "+" : ""}${d}% · ${this.t("prevMonth", { v: this.money(prev) })}`;
    },
    deltaClass(cur, prev) {
      if (!prev) return "is-neutral";
      return cur >= prev ? "is-up" : "is-down";
    },
    monthLabel(key) {
      const [y, m] = String(key).split("-").map((x) => parseInt(x, 10));
      const d = new Date(Date.UTC(y, m - 1, 1));
      return new Intl.DateTimeFormat(this.locale, { month: "short", timeZone: "UTC" }).format(d).replace(".", "");
    },
    shortDate(ymd) {
      const [, m, d] = String(ymd || "").slice(0, 10).split("-");
      return d && m ? `${d}/${m}` : "";
    },
    itemLink(basis, id, label) {
      const model = basis === "shippings" ? "shipping" : "proform";
      return `/i/${buildDynamicItemPrettySlug(model, id, label)}`;
    },
    beforeRefresh() {},
    onMenu(key) {
      if (key.startsWith("basis:")) this.$emit("update-config", { basis: key.slice(6) });
      else if (key.startsWith("currency:")) this.$emit("update-config", { currency: key.slice(9) });
      else if (key === "open") this.$router.push(this.openPath);
    },
  },
};
</script>

<style scoped>
.fw { display: flex; flex-direction: column; gap: 10px; height: 100%; min-height: 0; }
.fw-month { display: flex; flex-direction: column; gap: 6px; }
.fw-month + .fw-month { padding-top: 8px; border-top: 1px solid var(--dw-border); }
.fw-month__cur { font-size: 0.72rem; font-weight: 600; color: var(--dw-muted); letter-spacing: 0.04em; }
.fw-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 8px; }
.fw-kpi {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--dw-surface-2);
  color: var(--dw-text);
  text-decoration: none;
  min-width: 0;
}
a.fw-kpi:hover { color: var(--dw-text); filter: brightness(0.97); }
.fw-kpi__label { font-size: 0.72rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fw-kpi__value { font-size: 1.15rem; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fw-kpi__delta { font-size: 0.7rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fw-kpi__delta.is-up { color: var(--bs-success); }
.fw-kpi__delta.is-down { color: var(--bs-danger); }
.fw-caption { display: flex; align-items: center; gap: 12px; font-size: 0.75rem; color: var(--dw-muted); flex-wrap: wrap; }
.fw-legend { display: inline-flex; align-items: center; gap: 5px; }
.fw-sw { display: inline-block; width: 10px; height: 10px; border-radius: 3px; }
.fw-sw--in, .fw-pair__bar--in { background: var(--bs-primary); }
.fw-sw--out, .fw-pair__bar--out { background: var(--dw-line-strong); }
.fw-pairs { flex: 1; min-height: 110px; display: flex; gap: 6px; align-items: stretch; }
.fw-pair { flex: 1; display: flex; flex-direction: column; align-items: center; min-width: 0; }
.fw-pair__net { font-size: 0.65rem; color: var(--dw-muted); white-space: nowrap; }
.fw-pair__net.is-neg { color: var(--bs-danger); }
.fw-pair__bars { flex: 1; width: 100%; display: flex; align-items: flex-end; justify-content: center; gap: 2px; border-bottom: 1px solid var(--dw-border); }
.fw-pair__bar { width: 42%; max-width: 18px; border-radius: 4px 4px 0 0; }
.fw-pair__label { font-size: 0.65rem; color: var(--dw-muted); padding-top: 2px; text-transform: capitalize; }
.fw-chart { flex: 1; min-height: 120px; }
.fw-totals { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 8px; }
.fw-total {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--dw-surface-2);
  color: var(--dw-text);
  text-decoration: none;
}
a.fw-total:hover { color: var(--dw-text); filter: brightness(0.97); }
.fw-total__value { font-size: 1.2rem; font-weight: 700; }
.fw-total__value small { font-size: 0.7rem; font-weight: 600; color: var(--dw-muted); }
.fw-total__label { font-size: 0.72rem; color: var(--dw-muted); }
.fw-total__warn { font-size: 0.72rem; color: var(--bs-danger); font-weight: 600; }
.fw-list { list-style: none; margin: 0; padding: 0; }
.fw-row { display: flex; align-items: center; gap: 10px; padding: 7px 0; border-bottom: 1px solid var(--dw-border); }
.fw-row:last-child { border-bottom: 0; }
.fw-row__date { font-size: 0.75rem; font-weight: 600; min-width: 40px; color: var(--dw-muted); }
.fw-row__date.is-late { color: var(--bs-danger); }
.fw-row__pct { font-size: 0.78rem; font-weight: 700; min-width: 52px; text-align: right; }
.fw-row__pct.is-low { color: var(--bs-warning); }
.fw-row__pct.is-neg { color: var(--bs-danger); }
.fw-row__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.fw-row__title { font-weight: 600; font-size: 0.84rem; color: var(--dw-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
a.fw-row__title:hover { color: var(--bs-primary); }
.fw-row__text { font-size: 0.74rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fw-row__amount { font-size: 0.82rem; font-weight: 600; white-space: nowrap; }
.fw-row__amount small { font-size: 0.66rem; color: var(--dw-muted); }
.fw-ok { margin: 0; padding: 10px 0; text-align: center; font-size: 0.85rem; color: var(--dw-muted); }
.fw-ok i { color: var(--bs-success); margin-right: 4px; }
.fw-note { margin: 0; font-size: 0.7rem; color: var(--dw-muted); }
</style>
